/* =============================================================================
   The application's strings, in English
   -----------------------------------------------------------------------------
   This is the SOURCE catalogue: `pt-BR` is typed as `Record<keyof typeof en,
   string>`, so a key added here and forgotten there is a compile error rather
   than a screen that quietly stays English.

   Keys are namespaced by the area that renders them, and a key belongs to the
   namespace of the screen it appears on rather than to the word it contains —
   "Projects" as a sidebar entry and "Projects" as a page heading are two keys,
   because the two are read in different places and a shared one could only ever
   be edited for both at once.

   The design system's own `ds.*` strings are NOT here: it ships its English base
   and the provider merges it underneath. See `design-system/src/i18n/`.

   What is deliberately NOT here: the sample dataset's own words. Its Portuguese
   lives in `fixtures.pt-BR.ts`, keyed by each record's stable id rather than by
   the screen that renders it, and it is resolved through `useFixtureT`. It is
   not here because its ENGLISH is not here either — the English of a fixture
   string is the fixture string, so a copy in this file could only ever be a
   second one to keep in step. Project, investor and contractor names, addresses
   and the raw enum codes are proper nouns and wire values, and are in no
   catalogue in either language.
   ============================================================================= */

export const en = {
  /* ---- Shared vocabulary ---------------------------------------------------
     Words that mean the same thing on every screen that uses them. A string
     whose English differs from one of these — "Active holder" beside "Active" —
     belongs to its own screen under that screen's namespace instead. */
  'common.all': 'All',
  'common.search': 'Search',
  'common.date': 'Date',
  'common.amount': 'Amount',
  'common.status': 'Status',
  'common.total': 'Total',

  /* ---- Domain vocabulary ---------------------------------------------------
     The labels the dataset's enum codes are shown as. They are interface text:
     `'in-review'` is a wire value, "In review" is what a reader sees. */
  'status.phase.planned': 'Planned',
  'status.phase.completed': 'Completed',
  'status.phase.inProgress': 'In progress',
  'status.publication.draft': 'Draft',
  'status.publication.inReview': 'In review',
  'status.publication.approved': 'Approved',
  'status.publication.published': 'Published',
  'status.verification.verified': 'Verified',
  'status.verification.inReview': 'In review',
  'status.verification.rejected': 'Rejected',
  'status.participation.confirmed': 'Confirmed',
  'status.participation.pending': 'Pending',
  'status.participation.draft': 'Draft',
  'status.investor.active': 'Active',
  'status.investor.prospect': 'Prospect',
  'status.investor.closed': 'Closed',
  'status.investorKind.fund': 'Fund',
  'status.investorKind.individual': 'Individual',
  'status.investorKind.trust': 'Trust',
  'status.evidenceKind.photo': 'Photo',
  'status.evidenceKind.video': 'Video',
  'status.evidenceKind.note': 'Note',
  'status.documentType.invoice': 'Invoice',
  'status.documentType.receipt': 'Receipt',
  'status.documentType.contract': 'Contract',
  'status.documentType.permit': 'Permit',
  'status.documentType.quote': 'Quote',
  'status.documentType.report': 'Report',
  'status.documentType.inspection': 'Inspection',
  'status.lifecycle.capitalRaising': 'Preparing to raise',
  'status.lifecycle.acquisition': 'Under acquisition',
  'status.lifecycle.construction': 'Under construction',
  'status.lifecycle.viability': 'Under viability study',
  'status.lifecycle.opportunity': 'Sourcing',
  'status.lifecycle.saleAndClose': 'At sale',

  /* ---- Navigation ----------------------------------------------------------
     One registry, read by the sidebar, the portal's bar, the page headings and
     the document title, so a route cannot be named two things. */
  'nav.group.overview': 'Overview',
  'nav.group.delivery': 'Delivery',
  'nav.group.money': 'Money',
  'nav.group.relationships': 'Relationships',
  'nav.group.publishing': 'Publishing',
  'nav.group.administration': 'Administration',
  'nav.platform.dashboard': 'Dashboard',
  'nav.platform.dashboardHint': 'Portfolio at a glance',
  'nav.platform.opportunities': 'Opportunities',
  'nav.platform.opportunitiesHint': 'Pipeline and viability',
  'nav.platform.projects': 'Projects',
  'nav.platform.projectsHint': 'Every development',
  'nav.platform.schedule': 'Schedule',
  'nav.platform.scheduleHint': 'Phases and milestones',
  'nav.platform.evidence': 'Evidence',
  'nav.platform.evidenceHint': 'Photos, notes and files',
  'nav.platform.financials': 'Financials',
  'nav.platform.financialsHint': 'Aportes, expenses, balances',
  'nav.platform.suppliers': 'Suppliers',
  'nav.platform.suppliersHint': 'Vendors and partners',
  'nav.platform.investors': 'Investors',
  'nav.platform.investorsHint': 'Participations and access',
  'nav.platform.publications': 'Publications',
  'nav.platform.publicationsHint': 'Review, approve, release',
  'nav.platform.reports': 'Reports',
  'nav.platform.reportsHint': 'Generated from the base',
  'nav.platform.notifications': 'Notifications',
  'nav.platform.notificationsHint': 'What investors were told',
  'nav.platform.permissions': 'Permissions',
  'nav.platform.permissionsHint': 'Roles and controls',
  'nav.portal.projects': 'My projects',
  'nav.portal.projectsHint': 'Your participations',
  'nav.portal.updates': 'Updates',
  'nav.portal.updatesHint': 'Published reports',
  'nav.portal.documents': 'Documents',
  'nav.portal.documentsHint': 'Authorised files',
  'nav.portal.opportunities': 'Opportunities',
  'nav.portal.opportunitiesHint': 'Future projects',
  'nav.section.platform': 'Platform sections',
  'nav.section.portal': 'Investor portal sections',
  'nav.account': 'Account',
  'nav.signOut': 'Sign out',
  /** A screen with no registry entry still has to be named as a noun. */
  'nav.fallback': 'Project',
  'nav.signIn': 'Sign in',
  'nav.signingIn': 'Signing in',
  'nav.tab.overview': 'Overview',
  'nav.tab.schedule': 'Schedule',
  'nav.tab.financials': 'Financials',
  'nav.tab.evidence': 'Evidence',
  'nav.tab.investors': 'Investors',
  'nav.tab.publications': 'Publications',

  /* ---- Platform shell ----------------------------------------------------- */
  'shell.notifications.none': 'No unread notifications.',
  'shell.notifications.unread.one': '{count} unread notification.',
  'shell.notifications.unread.other': '{count} unread notifications.',
  'shell.notifications.markAllRead': 'Mark all as read',
  'shell.notifications.viewAll': 'View all',
  'shell.notifications.nothing': 'Nothing to report.',

  /* ---- Investor portal shell ---------------------------------------------- */
  'shell.portal.tagline': 'Investor portal',
  'shell.portal.access': 'Investor access',
  'shell.portal.accountNamed': 'Account: {name}',
  'shell.portal.role': 'Investor',

  /* ---- Project shell -------------------------------------------------------
     The frame every project-scoped screen renders inside. Its headline figures
     are the ones the six tabs have to agree on. */
  'project.shell.notFoundTitle': 'Project not found',
  'project.shell.notFoundHeading': 'No such development',
  'project.shell.notFoundMessage':
    'Nothing matches “{id}”. It may have been closed or archived.',
  'project.shell.sale': 'Sale {date}',
  'project.shell.next': 'Next: {phase} · planned to start {date}',
  'project.shell.progress': '{name} — overall progress',
  'project.shell.sections': '{name} sections',
  'project.shell.committed': 'Committed capital',
  'project.shell.contributed': 'Contributed',
  'project.shell.spent': 'Spent to date',
  'project.shell.variance': 'Variance on work started',
  'project.shell.uncalled': '{amount} not yet called',
  'project.shell.fullyCalled': 'Fully called',
  'project.shell.unspent': '{amount} unspent',
  'project.shell.varianceBasis': 'against {amount} budgeted for phases under way',

  /* ---- Sign-in ------------------------------------------------------------ */
  'login.skip': 'Skip to login',
  'login.heading': 'Sign in',
  'login.continueGoogle': 'Continue with Google',
  'login.continueApple': 'Continue with Apple',
  'login.continueMicrosoft': 'Continue with Microsoft',
  'login.emailSwitch': 'Login with Email and Password',
  'login.email': 'Email',
  'login.emailPlaceholder': 'you@example.com',
  'login.password': 'Password',
  'login.forgot': 'Forgot password?',
  'login.submit': 'Login',
  'login.backToSocial': 'Back to social login',
  'login.resetHeading': 'Reset your password',
  'login.sendResetLink': 'Send reset link',
  'login.backToLogin': 'Back to login',
  'login.notConnectedTitle': 'Email sign-in is not connected',
  'login.notConnectedBody':
    'Nothing was sent for {email}. Continue with Google, Microsoft or Apple.',
  'login.resetNotSentTitle': 'Reset link not sent',
  'login.resetNotSentBody':
    'This build has no password-reset service connected, so nothing was sent to {email}.',

  /* ---- Provider callback -------------------------------------------------- */
  'auth.signingInHeading': 'Signing in',
  'auth.signingIn': 'Signing you in',
  'auth.failedTitle': 'Sign-in not completed',
  'auth.emptyTitle': 'No sign-in result',
  'auth.emptyMessage': 'Sign in from the escrowbuild.io home page to continue.',
  'auth.backToSignIn': 'Back to sign in',
  'auth.providerReturned': 'The provider returned {reason}. Sign in again to continue.',
  'auth.notCompleted': 'Sign-in was not completed. Sign in again to continue.',
  'auth.unverified':
    'This sign-in could not be verified against the one that was started, so it was not completed. Sign in again to continue.',
  'auth.networkError': 'Could not reach the server. Check your connection and try again.',
  'auth.exchangeFailed': 'Sign-in could not be completed (HTTP {status}).',

  /* ---- 404 ---------------------------------------------------------------- */
  'notFound.heading': 'Page not found',
  'notFound.title': 'This page does not exist',
  'notFound.description':
    'The address you followed is not one of the platform’s screens. It may have been renamed since the link was saved, or the address may have been mistyped.',
  'notFound.dashboard': 'Back to the dashboard',
  'notFound.portal': 'Open the investor portal',

  /* ---- Opportunities ------------------------------------------------------ */
  'opportunities.title': 'Opportunities',
  'opportunities.scaffold': 'Scaffold — content to be filled in.',
  'opportunities.placeholder': 'Placeholder.',

  /* ==== GROUPED CATALOGUE ENTRIES (merged) ==== */

  /* ---- Dashboard, projects and the project overview (GA) ---- */
  'dashboard.title': 'Portfolio dashboard',
  'dashboard.subtitle.one':
    '{count} development under management — capital, spend and publication status across the portfolio.',
  'dashboard.subtitle.other':
    '{count} developments under management — capital, spend and publication status across the portfolio.',
  'dashboard.committed': 'Total committed capital',
  'dashboard.totalSpent': 'Total spent',
  'dashboard.unspent': 'Total unspent balance',
  'dashboard.active': 'Active projects',
  'dashboard.committedHint': '{amount} contributed so far',
  'dashboard.unspentHint': 'Contributed capital less what has actually been spent.',
  'dashboard.varianceHint': 'Against {amount} budgeted across the phases under way.',
  'dashboard.activeHint': 'Developments with a phase currently in progress.',
  'dashboard.showAlert': 'Show alert again',
  'dashboard.attention.title.one': '{count} item cannot reach investors yet',
  'dashboard.attention.title.other': '{count} items cannot reach investors yet',
  'dashboard.attention.draft': '{project} — "{title}" is still a draft for {period}.',
  'dashboard.attention.inReview': '{project} — "{title}" is in review for {period}.',
  'dashboard.attention.expense': '{project} — {vendor} for {amount} is awaiting financial review.',
  'dashboard.attention.document': '{project} — "{name}" was rejected and has to be replaced.',
  'dashboard.progress': 'Overall progress',
  'dashboard.spent': 'Spent',
  'dashboard.balance': 'Balance',
  'dashboard.updates.title': 'Recent published updates',
  'dashboard.updates.description': 'The five most recent updates cleared for the investor portal.',
  'projects.title': 'Projects',
  'projects.subtitle.one':
    'The portfolio — {count} live development, with committed capital, spend to date, balance and variance against the phased budget.',
  'projects.subtitle.other':
    'The portfolio — {count} live developments, with committed capital, spend to date, balance and variance against the phased budget.',
  'projects.panelTitle': 'Portfolio',
  'projects.panelDescription':
    "Every figure is derived from the project's own schedule and participations.",
  'projects.searchLabel': 'Search projects',
  'projects.searchPlaceholder': 'Name, address or city',
  'projects.phase': 'Phase',
  'projects.matched.one': '{matched} of {count} project',
  'projects.matched.other': '{matched} of {count} projects',
  'projects.clearFilters': 'Clear filters',
  'projects.column.address': 'Address / City',
  'projects.column.progress': 'Progress',
  'projects.column.spent': 'Spent',
  'projects.column.balance': 'Balance',
  'projects.column.variance': 'Variance',
  'projects.column.projectedSale': 'Projected sale',
  'projects.sort.asc': 'Sorted ascending. Activate to sort descending.',
  'projects.sort.desc': 'Sorted descending. Activate to clear the sort.',
  'projects.sort.none': 'Sort ascending.',
  'projects.empty.title': 'No projects match these filters',
  'projects.empty.description':
    'Nothing in the portfolio matches the current search and phase. Clear the filters to see all {count} developments.',
  'projects.caption':
    'The portfolio: each development with its phase, progress, committed capital, spend to date, unspent balance, variance against the phased budget and projected sale.',
  'project.overview.notFoundMessage':
    'Nothing matches "{id}". It may have been closed or archived.',
  'project.overview.currentPhase.title': 'Current phase',
  'project.overview.currentPhase.description':
    "Span positions are months on the schedule's 12-month axis.",
  'project.overview.plannedSpan': 'Planned span',
  'project.overview.actualSpan': 'Actual span',
  'project.overview.notStarted': 'Not started',
  'project.overview.budgeted': 'Budgeted',
  'project.overview.variance': 'Variance',
  'project.overview.evidenceFiled': 'Evidence filed',
  'project.overview.evidenceCount.one': '{count} item',
  'project.overview.evidenceCount.other': '{count} items',
  'project.overview.evidenceCleared.one': '{count} cleared for the portal',
  'project.overview.evidenceCleared.other': '{count} cleared for the portal',
  'project.overview.months.one': '{months} month long',
  'project.overview.months.other': '{months} months long',
  'project.overview.spendProgress': '{phase} — spend against the phased budget',
  'project.overview.noBudget': 'This phase carries no budget of its own.',
  'project.overview.noPhase.title': 'No phase to report',
  'project.overview.noPhase.description':
    "This development's schedule has neither a running nor a planned phase.",
  'project.overview.milestones.title': 'Next milestones',
  'project.overview.milestones.description': 'Soonest first.',
  'project.overview.milestones.list': 'Upcoming milestones',
  'project.overview.milestones.empty': 'No milestones are scheduled ahead.',
  'project.overview.phasesLink.one': 'See all {count} phase on the schedule',
  'project.overview.phasesLink.other': 'See all {count} phases on the schedule',
  'project.overview.investorNotified': 'Investor notified',
  'project.overview.activity.title': 'Recent activity',
  'project.overview.activity.description': 'The five most recent entries.',
  'project.overview.activity.published': 'Published update',
  'project.overview.activity.milestone': 'Milestone reached',
  'project.overview.activity.empty':
    'Nothing has been published and no milestone has been reached yet.',
  'project.overview.participation.title': 'Investor participation',
  'project.overview.participation.description.one':
    '{count} holding on {project}, and what each has paid in.',
  'project.overview.participation.description.other':
    '{count} holdings on {project}, and what each has paid in.',
  'project.overview.participation.emptyTitle': 'No investor holdings',
  'project.overview.participation.emptyDescription':
    'No participation carrying a share is recorded against this development.',
  'project.overview.participation.caption':
    "Investor participations on this development, with each holding's share, committed and contributed capital.",
  'project.overview.participation.summary':
    '{investorShare} held by the investors above plus {ownership} retained by the builder is {total} of the development. Contributions total {contributed}, against committed capital of {committed}.',
  'project.overview.column.investor': 'Investor',
  'project.overview.column.share': 'Share',
  'project.overview.column.committed': 'Committed',
  'project.overview.column.document': 'Document',
  'project.overview.column.type': 'Type',
  'project.overview.column.review': 'Review',
  'project.overview.column.publication': 'Publication',
  'project.overview.investorVisible': 'Investor-visible',
  'project.overview.internalOnly': 'Internal only',
  'project.overview.documents.title': 'Latest documents',
  'project.overview.documents.description.one':
    '{count} on file. Only documents that passed review reach the investor.',
  'project.overview.documents.description.other':
    '{count} on file. Only documents that passed review reach the investor.',
  'project.overview.documents.filterLabel': 'Filter documents by review state',
  'project.overview.documents.emptyTitle': 'Nothing is {state}',
  'project.overview.documents.emptyDescription':
    'Every document filed against this development sits in another review state.',
  'project.overview.documents.showAll': 'Show all documents',
  'project.overview.documents.caption':
    'The most recent documents on this development, with their type, date, review state and whether the investor can see them.',
  'project.overview.documents.showing.one': 'Newest {shown} of {count} document in this state.',
  'project.overview.documents.showing.other': 'Newest {shown} of {count} documents in this state.',

  /* ---- Schedule and evidence (GB) ---- */
  'schedule.title': 'Schedule',
  'schedule.subtitle':
    'Every development’s phases on one axis, planned against actual. The plan is not a sequence — phases run in parallel, and the overlap is drawn rather than hidden.',
  'schedule.axis.month': 'Month {month}',
  'schedule.metric.complete': 'Phases complete',
  'schedule.metric.completeHint': 'Across {name}.',
  'schedule.metric.concurrent': 'Concurrent fronts',
  'schedule.metric.concurrentHint': 'Phases running at once around month {month} of the plan.',
  'schedule.metric.noOverlap': 'No two phases overlap in this plan.',
  'schedule.pastPlannedEnd': 'Past the planned end',
  'schedule.metric.lateHint': 'Phases whose actual span runs past the plan.',
  'schedule.timeline.title': 'Timeline',
  'schedule.timeline.description':
    'Positions in the fixture are percentages of a 12-month span; the axis is labelled in months from each project’s schedule start.',
  'schedule.development': 'Development',
  'schedule.forecast': '{name} · {city} · sale forecast {date}',
  'schedule.hideMilestones': 'Hide milestones',
  'schedule.showMilestones': 'Show milestones',
  'schedule.peak.title.one': '{count} phase runs at the same time around month {month}',
  'schedule.peak.title.other': '{count} phases run at the same time around month {month}',
  'schedule.peak.description':
    '{names}. The document’s §10 is explicit that phases overlap: electrical and plumbing run together, framing advances while another area is sided, and drywall proceeds on one floor while framing continues on another.',
  'schedule.late.title.one': '{count} phase extended past the planned end',
  'schedule.late.title.other': '{count} phases extended past the planned end',
  'schedule.late.item': '{name} by {months} months',
  'schedule.noLate.title': 'Every started phase closed inside its planned window',
  'schedule.noLate.description': 'No actual span runs past its planned end date.',
  'schedule.noProject': 'No development selected.',
  'schedule.plannedVsActual.title': 'Planned against actual',
  'schedule.plannedVsActual.description':
    'One row per phase, with the drift and the money behind it.',
  'schedule.table.caption': '{name} phases: planned and actual windows, drift, budget and spend',
  'schedule.col.phase': 'Phase',
  'schedule.col.plannedWindow': 'Planned window',
  'schedule.col.actualWindow': 'Actual window',
  'schedule.col.drift': 'Drift',
  'schedule.col.budgeted': 'Budgeted',
  'schedule.col.actual': 'Actual',
  'schedule.col.variance': 'Variance',
  'schedule.col.spend': 'Spend',
  'schedule.notStarted': 'Not started',
  'schedule.drift.value': '{value} mo',
  'schedule.table.note':
    'Drift is the actual span measured against the planned one: a positive figure is a phase that ran past its plan, a negative one finished early. The last column is spend against that phase’s own budget, not the project’s.',
  'schedule.milestones.title': 'Milestones',
  'schedule.milestones.description': 'Dates and investor notification for {name}.',
  'schedule.milestones.selected': 'the selected development',
  'schedule.milestones.empty': 'No milestones recorded for this development.',
  'schedule.milestone.reached': 'Reached',
  'schedule.milestone.upcoming': 'Upcoming',
  'schedule.milestone.notified': 'Investor notified',
  'schedule.milestone.notNotified': 'Not notified',
  'schedule.portfolio.title': 'Portfolio',
  'schedule.portfolio.description':
    'The same figures for every development, so the three can be read against each other.',
  'schedule.portfolio.caption':
    'Every development: phase counts, budget, spend and phases running late',
  'schedule.portfolio.col.development': 'Development',
  'schedule.portfolio.col.done': 'Done',
  'schedule.portfolio.col.running': 'Running',
  'schedule.portfolio.col.planned': 'Planned',
  'schedule.portfolio.col.budgeted': 'Budgeted',
  'schedule.portfolio.col.actual': 'Actual',
  'schedule.portfolio.col.late': 'Late',
  'schedule.gantt.planned': 'planned {window}',
  'schedule.gantt.notStarted': 'not started',
  'schedule.gantt.actual': 'actual {window}',
  'schedule.gantt.late': '{months} months past the plan',
  'schedule.gantt.budgeted': 'Budgeted {amount}',
  'schedule.gantt.spent': 'Spent {amount}',
  'schedule.legend.title': 'Key',
  'schedule.startedEarly': 'Started early',
  'schedule.legend.milestone': 'Milestone',
  'schedule.legend.overlap': 'Overlapping rows are phases running at the same time',
  'project.schedule.filter.all': 'All phases',
  'project.schedule.timeline.title': 'Phase timeline',
  'project.schedule.timeline.description':
    'Every phase on one axis. The row order is the record’s order, not the build order — phases are expected to overlap.',
  'project.schedule.filterLabel': 'Filter phases by status',
  'project.schedule.empty.title': 'No phases recorded',
  'project.schedule.empty.description':
    'This development has no schedule yet, so there is nothing to place on the axis.',
  'project.schedule.col.phase': 'Phase',
  'project.schedule.noMatch': 'No phases have this status.',
  'project.schedule.legend.actual': 'Actual',
  'project.schedule.legend.reached': 'Milestone reached',
  'project.schedule.legend.upcoming': 'Milestone upcoming',
  'project.schedule.note.axis':
    'Positions are percentages of a 12-month axis — one division is one month.',
  'project.schedule.note.reach':
    'This schedule reaches month {month}, so the axis is drawn out to cover it.',
  'project.schedule.note.spent': '{value} of the phased budget has been spent.',
  'project.schedule.parallel.title': 'Running in parallel',
  'project.schedule.parallel.none': 'No two phases in this view share a working window.',
  'project.schedule.parallel.summary.one':
    '{count} overlapping pair across the {phases} phases shown; at most {fronts} fronts open at the same time.',
  'project.schedule.parallel.summary.other':
    '{count} overlapping pairs across the {phases} phases shown; at most {fronts} fronts open at the same time.',
  'project.schedule.parallel.rules':
    'Each phase is read as its actual window once it has started, its planned window before that.',
  'project.schedule.parallel.with': 'with',
  'project.schedule.budget.title': 'Phase budget',
  'project.schedule.budget.description':
    'Budget against actual for every phase, so the total reconciles with the figure the project shell prints.',
  'project.schedule.budget.caption': 'Budgeted and actual cost per phase, with the project total.',
  'project.schedule.col.budgeted': 'Budgeted',
  'project.schedule.col.actual': 'Actual',
  'project.schedule.col.variance': 'Variance',
  'project.schedule.total': 'Project total',
  'project.schedule.amount.budgeted': '{amount} budgeted',
  'project.schedule.amount.actual': '{amount} actual',
  'project.schedule.amount.variance': '{amount} variance',
  'project.schedule.sr.planned': 'Planned months {from} to {to}.',
  'project.schedule.sr.actual': 'Actual months {from} to {to}.',
  'project.schedule.sr.notStarted': 'Not started.',
  'project.schedule.sr.figures': 'Budgeted {budgeted}, actual {actual}, variance {variance}.',
  'project.schedule.sr.milestones': 'Milestones: {list}.',
  'project.schedule.sr.milestone': '{title} ({date}, {status})',
  'project.schedule.milestone.reached': 'reached',
  'project.schedule.milestone.upcoming': 'upcoming',
  'project.schedule.notStarted': 'Not started',
  'project.schedule.month': 'month {month}',
  'project.schedule.monthRange': 'months {from}–{to}',
  'evidence.title': 'Evidence',
  'evidence.subtitle':
    'Every photo, note and file the site has captured. Capture and release are two different decisions: a document can be verified internally and still not be visible to an investor.',
  'evidence.metric.items': 'Evidence items',
  'evidence.metric.itemsHint': '{released} released to investors.',
  'evidence.metric.documents': 'Documents on file',
  'evidence.metric.documentsHint': '{released} released across {projects} developments.',
  'evidence.metric.awaiting': 'Awaiting publication',
  'evidence.metric.awaitingHint': 'Photos and documents captured but not yet released.',
  'evidence.metric.verifiedUnreleased': 'Verified but not released',
  'evidence.metric.verifiedUnreleasedHint':
    'Passed internal verification, held back from the portal.',
  'evidence.gate.title': 'Publication gate',
  'evidence.gate.description':
    'What is captured is not what the investor sees — release is a separate act, and nothing reaches the portal until it is.',
  'evidence.gate.showEverything': 'Show everything',
  'evidence.gate.showOnlyUnpublished': 'Show only unpublished',
  'evidence.gate.alert.title': '{awaiting} of {total} records are not yet published',
  'evidence.gate.alert.description':
    '{documents} documents and {evidence} evidence items are captured internally and invisible to investors. Of those documents, {verified} already passed verification — being verified and being released are two separate states, and only the second one opens the portal.',
  'evidence.gate.phase.title': 'Every document is attached to a phase',
  'evidence.gate.phase.description':
    'All {documents} documents carry a project and a phase, so none of them is held back by the phase-association rule.',
  'evidence.gate.noPhase.title.one': '{count} document has no phase',
  'evidence.gate.noPhase.title.other': '{count} documents have no phase',
  'evidence.gate.noPhase.description': '{names}. A record without a phase cannot be published.',
  'evidence.gate.heldBack': 'Held back',
  'evidence.notReleased': 'Not released',
  'evidence.notPublished': 'Not published',
  'evidence.filters.title': 'Filters',
  'evidence.filters.description': 'These narrow the gallery and the register below together.',
  'evidence.filter.project': 'Project',
  'evidence.filter.allProjects': 'All projects',
  'evidence.filter.publication': 'Publication',
  'evidence.filter.anyState': 'Any state',
  'evidence.filter.phase': 'Phase',
  'evidence.filter.allPhases': 'All phases',
  'evidence.filter.kind': 'Kind',
  'evidence.filter.allKinds': 'All kinds',
  'evidence.showing':
    'Showing {evidence} of {totalEvidence} evidence items and {documents} of {totalDocuments} documents.',
  'evidence.clearFilters': 'Clear filters',
  'evidence.gallery.title': 'Photos and notes',
  'evidence.gallery.description': '{shown} of {total} captured records match the filters above.',
  'evidence.gallery.empty.title': 'No evidence matches these filters',
  'evidence.gallery.empty.description':
    'Nothing was captured for this project, phase and state together.',
  'evidence.gallery.showing': 'Showing {shown} of {total} matching evidence items.',
  'evidence.gallery.showFewer': 'Show fewer',
  'evidence.gallery.showAll': 'Show all {total}',
  'evidence.capturedBy': 'captured by {name}',
  'evidence.withdraw': 'Withdraw',
  'evidence.publish': 'Publish',
  'evidence.withdrawAria': 'Withdraw {caption} from the investor portal',
  'evidence.publishAria': 'Publish {caption} to the investor portal',
  'evidence.toast.released': 'Released to investors',
  'evidence.toast.withdrawn': 'Withdrawn from the portal',
  'evidence.toast.description': '{caption} — {phase}.',
  'evidence.noPhase': 'No phase',
  'evidence.documents.title': 'Documents',
  'evidence.documents.description':
    'Every invoice, receipt, contract, permit, quote, report and inspection on file. Verification and release are separate columns on purpose — the table can show a verified document that is still not released.',
  'evidence.documents.caption': 'Documents on file, with verification state and release state',
  'evidence.documents.emptyTitle': 'No documents match the current filters',
  'evidence.documents.emptyDescription':
    'Clear the project, phase or publication filters above to see the full register.',
  'evidence.col.document': 'Document',
  'evidence.col.project': 'Project',
  'evidence.col.phase': 'Phase',
  'evidence.col.type': 'Type',
  'evidence.col.verification': 'Verification',
  'evidence.col.release': 'Release',
  'evidence.col.size': 'Size',
  'evidence.col.uploadedBy': 'Uploaded by',
  'project.evidence.metric.captured': 'Photos and notes captured',
  'project.evidence.metric.released': 'Released to investors',
  'project.evidence.metric.releasedHint': 'Visible in the portal today.',
  'project.evidence.metric.awaiting': 'Awaiting publication',
  'project.evidence.metric.awaitingHint': 'Captured internally, held back from the portal.',
  'project.evidence.metric.documents': 'Documents on file',
  'project.evidence.metric.documentsHint': '{unreleased} of them not released.',
  'project.evidence.capturedBy': 'captured by {name}',
  'project.evidence.released': 'Released',
  'project.evidence.notReleased': 'Not released',
  'project.evidence.stagedNotUploaded': 'staged, not uploaded',
  'project.evidence.removeStaged': 'Remove {name} from the staged uploads',
  'project.evidence.phase.released': '{released} of {total} released',
  'project.evidence.phase.documents.one': '{count} document',
  'project.evidence.phase.documents.other': '{count} documents',
  'project.evidence.phase.staged': '{count} staged',
  'project.evidence.phase.counts':
    '{captured} captured · {released} released · {awaiting} awaiting publication',
  'project.evidence.progress': '{phase} — evidence released to investors',
  'project.evidence.photosAndNotes': 'Photos and notes',
  'project.evidence.documentsHeading': 'Documents',
  'project.evidence.stagedHeading': 'Staged in this session',
  'project.evidence.list.evidence': '{phase} evidence',
  'project.evidence.list.documents': '{phase} documents',
  'project.evidence.list.staged': '{phase} staged uploads',
  'project.evidence.empty.title': 'Nothing captured in this phase yet',
  'project.evidence.empty.description':
    'Photos, videos, notes and documents can be attached to a phase as soon as the work starts — §14 only requires that they are attached to the right one before they are published.',
  'project.evidence.attachFiles': 'Attach files',
  'project.evidence.toast.staged.one': '{count} file staged',
  'project.evidence.toast.staged.other': '{count} files staged',
  'project.evidence.toast.stagedBody':
    'Attached to {phase}. Nothing is visible to an investor until it is released.',
  'project.evidence.upload.title': 'Add evidence',
  'project.evidence.upload.description':
    'Photos, videos, documents and notes are in scope for the first release — each one is attached to a phase before it can be published.',
  'project.evidence.upload.drop': 'Drop photos, videos or documents here.',
  'project.evidence.upload.choose': 'Choose files',
  'project.evidence.upload.attachTo': 'Attach to phase',
  'project.evidence.upload.captionLabel': 'Caption for this batch',
  'project.evidence.upload.captionDescription':
    'Used as the caption when the files are staged. Optional — the file name is used otherwise.',
  'project.evidence.upload.ready': 'Ready to stage',
  'project.evidence.nothingStaged': 'Nothing staged yet.',
  'project.evidence.readyFor.one': '{count} file ready for {phase}',
  'project.evidence.readyFor.other': '{count} files ready for {phase}',
  'project.evidence.clear': 'Clear',
  'project.evidence.stage.one': 'Stage {count} file',
  'project.evidence.stage.other': 'Stage {count} files',
  'project.evidence.staging.title': 'Staging is not publishing',
  'project.evidence.staging.description':
    'Files added here are attached to the phase and stay internal. An investor sees them only after the update that carries them has been through review and release.',
  'project.evidence.byPhase.title': 'Evidence by phase',
  'project.evidence.byPhase.description':
    'What was captured, what has been released, and the documents filed against each phase.',

  /* ---- Financials and suppliers (GC) ---- */
  'financials.title': 'Financials',
  'financials.subtitle':
    'Forecast against actual, and where the money went, across all three developments.',
  'financials.metrics.contributed': 'Contributed capital',
  'financials.metrics.contributedHint':
    'All three developments — investor capital actually paid in.',
  'financials.metrics.spent': 'Spent to date',
  'financials.metrics.spentHint': 'All three developments — posted to the expense ledger.',
  'financials.metrics.unspent': 'Unspent balance',
  'financials.metrics.unspentHint':
    'All three developments — committed capital less what has actually been spent.',
  'financials.metrics.variance': 'Variance vs budget',
  'financials.metrics.varianceHint':
    'All three developments — actual spend against the full phased budget, which includes phases not yet started.',
  'financials.chart.title': 'Spend against budget — Lakeview Residence',
  'financials.chart.description':
    'The chart series is the Lakeview development: monthly budgeted against actual spend, and budgeted against actual by construction phase. The headline figures above and the ledger below cover all three developments.',
  'financials.cash.title': 'Cash flow',
  'financials.cash.description':
    'Capital contributed against spent, month by month, with the running net and the closing position.',
  'financials.cash.caption':
    'Monthly cash flow — contributed against spent, with the net movement and the cumulative position.',
  'financials.cash.month': 'Month',
  'financials.cash.contributed': 'Contributed',
  'financials.cash.spent': 'Spent',
  'financials.cash.net': 'Net',
  'financials.cash.cumulative': 'Cumulative',
  'financials.ledger.title': 'Expense ledger',
  'financials.ledger.intro.one':
    'Every expense posted across all three developments — {count} line. The subtotal under each money column covers the whole filtered set, not the page on screen, so a filtered view reconciles without exporting it first.',
  'financials.ledger.intro.other':
    'Every expense posted across all three developments — {count} lines. The subtotal under each money column covers the whole filtered set, not the page on screen, so a filtered view reconciles without exporting it first.',
  'financials.ledger.caption': 'Expenses across every development',
  'financials.ledger.emptyTitle': 'No expenses match these filters',
  'financials.ledger.emptyDescription': 'Widen a filter, or clear them all and start again.',
  'financials.ledger.errorTitle': 'The expense ledger failed to load',
  'financials.variance.title': 'Reading the variance column',
  'financials.variance.body':
    'Forecast is the figure the phase was budgeted at, and variance is amount minus forecast. A negative line came in under its forecast, a positive one went over — so the lines that moved the budget are the ones furthest from zero.',
  'financials.column.ref': 'Ref',
  'financials.column.development': 'Development',
  'financials.column.phase': 'Phase',
  'financials.column.category': 'Category',
  'financials.column.vendor': 'Vendor',
  'financials.column.forecast': 'Forecast',
  'financials.column.variance': 'Variance',
  'financials.column.delivery': 'Delivery',
  'financials.column.description': 'Description',
  'financials.search.developments': 'Search developments…',
  'financials.search.phases': 'Search phases…',
  'financials.search.categories': 'Search categories…',
  'financials.search.vendors': 'Search vendors…',
  'financials.search.statuses': 'Search statuses…',
  'financials.search.delivery': 'Search delivery…',
  'financials.search.deliveryStates': 'Search delivery states…',
  'financials.expenseStatus.approved': 'Approved',
  'financials.expenseStatus.inReview': 'In review',
  'financials.expenseStatus.disputed': 'Disputed',
  'financials.delivery.delivered': 'Delivered',
  'financials.delivery.partial': 'Partial',
  'financials.delivery.pending': 'Pending',
  'financials.delivery.notApplicable': 'Not applicable',
  'project.financials.notFoundMessage':
    'Nothing matches "{id}". It may have been closed or archived.',
  'project.financials.phases.title': 'Phase-by-phase budget',
  'project.financials.phases.description':
    'The actuals below sum to the {amount} of spend to date shown above — the phase lines are that figure decomposed.',
  'project.financials.phases.caption':
    'Budget, actual, variance and budget consumed for each phase of {name}.',
  'project.financials.column.budgeted': 'Budgeted',
  'project.financials.column.actual': 'Actual',
  'project.financials.column.used': 'Used',
  'project.financials.phaseCount.one': '{count} phase',
  'project.financials.phaseCount.other': '{count} phases',
  'project.financials.budgetConsumed': '{name} — budget consumed',
  'project.financials.unverified.title.one': '{count} expense line is posted but not yet verified',
  'project.financials.unverified.title.other':
    '{count} expense lines are posted but not yet verified',
  'project.financials.unverified.description':
    'They total {amount}. The money is committed and carries into the figures above, but verification has not closed, so the phase totals can still move.',
  'project.financials.aheadOfDelivery.title.one': '{count} expense line is paid ahead of delivery',
  'project.financials.aheadOfDelivery.title.other':
    '{count} expense lines are paid ahead of delivery',
  'project.financials.aheadOfDelivery.description':
    'They total {amount}. Payment runs ahead of the work on these lines, so the spend above is not yet matched by what is on site.',
  'project.financials.disputed.title.one': '{count} expense line is disputed',
  'project.financials.disputed.title.other': '{count} expense lines are disputed',
  'project.financials.disputed.description':
    'They total {amount}. They are neither approved nor settled, and the spend above should be read as under challenge until they are.',
  'project.financials.ledger.description':
    'Every posted line behind the phase actuals. Filter or sort it and the footer subtotal follows — it covers the whole filtered set, not the page in view.',
  'project.financials.ledger.caption': 'Expense ledger for {name}',
  'project.financials.ledger.emptyTitle': 'No expense lines match these filters',
  'project.financials.funding.title': 'Funding schedule',
  'project.financials.funding.description':
    'Committed is the capital each participation subscribed for — a different figure from the {amount} of contributed capital, which is what the "Contributed" tile at the top of this page shows.',
  'project.financials.funding.caption':
    'The investors funding {name}, with share, committed and contributed capital, and the capital calls issued and settled.',
  'project.financials.column.investor': 'Investor',
  'project.financials.column.share': 'Share',
  'project.financials.column.committed': 'Committed',
  'project.financials.column.contributed': 'Contributed',
  'project.financials.column.callsIssued': 'Calls issued',
  'project.financials.column.callsSettled': 'Calls settled',
  'project.financials.investorCount.one': '{count} investor',
  'project.financials.investorCount.other': '{count} investors',
  'contractors.title': 'Suppliers',
  'contractors.subtitle':
    'Who is on the register, what has been awarded, what has been paid, and what the expense ledger has actually recorded against each supplier.',
  'contractors.suppliersOnFile': 'Suppliers on file',
  'contractors.suppliersOnFileHint': 'Every contractor and partner on the register.',
  'contractors.contractValue': 'Contract value',
  'contractors.contractValueHint': 'Sum of the awarded contract value across the register.',
  'contractors.paidToDate': 'Paid to date',
  'contractors.paidToDateHint': 'Sum of what has been paid against those awards.',
  'contractors.recordedSpend': 'Recorded spend',
  'contractors.recordedSpendHint':
    'Every expense line in the ledger, attributed to a supplier by vendor id.',
  'contractors.lookup.title': 'Supplier lookup',
  'contractors.lookup.description':
    'The search runs on the API side: the field sends the term to the remote source and renders what comes back.',
  'contractors.supplier': 'Supplier',
  'contractors.lookup.placeholder': 'Search suppliers by name or trade…',
  'contractors.lookup.hint':
    "Matches on the supplier's name and its trade — no filtering happens in the browser.",
  'contractors.detail.description':
    'The award as the register holds it, and the expense lines the ledger attributes to this supplier by vendor id.',
  'contractors.contractId': 'Contract ID',
  'contractors.outstanding': 'Outstanding',
  'contractors.crewSize': 'Crew size',
  'contractors.started': 'Started',
  'contractors.lastInspection': 'Last inspection',
  'contractors.approvedAt': 'Approved at',
  'contractors.verified': 'Verified',
  'contractors.yes': 'Yes',
  'contractors.no': 'No',
  'contractors.progress': '{name} — completion',
  'contractors.noSpend':
    'No expense line in this build resolves to {name}. The award is on the register, but nothing has been recorded against it in the ledger.',
  'contractors.spend.caption.one':
    'Expense lines attributed to {name} — {count} line, {amount} in total.',
  'contractors.spend.caption.other':
    'Expense lines attributed to {name} — {count} lines, {amount} in total.',
  'contractors.column.project': 'Project',
  'contractors.phase': 'Phase',
  'contractors.totalRecorded': 'Total recorded',
  'contractors.empty.title': 'No supplier selected',
  'contractors.empty.description':
    "Search the register above to see a supplier's award, the payments recorded against it and the expense lines the ledger attributes to it.",
  'contractors.register.title': 'Supplier register',
  'contractors.register.description':
    'Sorting, filtering, paging and the footer subtotals all run on the data source rather than in the browser, so each subtotal covers the whole filtered set and not only the page on screen.',
  'contractors.register.caption': 'Suppliers — award, spend and inspection register',
  'contractors.register.emptyTitle': 'No suppliers to show',
  'contractors.register.errorTitle': 'The supplier register failed to load',
  'contractors.register.searchTrades': 'Search trades…',
  'contractors.register.note':
    'The supplier record in this build carries no city, contact or document fields, so the register shows the award, spend and inspection data that exists.',
  'contractors.crew': 'Crew',
  'contractors.completion': 'Completion',
  'contractors.trade': 'Trade',

  /* ---- Investors and permissions (GD) ---- */
  'investors.title': 'Investors',
  'investors.intro':
    'Who holds a position in the portfolio, what they committed, and what has actually been called. A holding is a participation — the register carries the identity, the projects carry the money.',
  'investors.onRegister': 'On the register',
  'investors.prospectCount.one': '{count} prospect',
  'investors.prospectCount.other': '{count} prospects',
  'investors.closedCount.one': '{count} closed',
  'investors.closedCount.other': '{count} closed',
  'investors.committedHint': 'Sum of every participation, across all developments.',
  'investors.contributedToDate': 'Contributed to date',
  'investors.outstandingHint': '{amount} still outstanding across the funding schedules.',
  'investors.register': 'The register',
  'investors.registerHint':
    'Open an investor for their participations, their documents and what they have read.',
  'investors.kind': 'Kind',
  'investors.kindFilter': 'Filter by investor kind',
  'investors.statusFilter': 'Filter by status',
  'investors.city': 'City',
  'investors.cityFilter': 'Filter by city',
  'investors.allCities': 'All cities',
  'investors.searchLabel': 'Search the investor register',
  'investors.searchPlaceholder': 'Name, email or tax id',
  'investors.tableCaption':
    "The investor register, with the projects, committed and contributed capital derived from each investor's participations.",
  'investors.colInvestor': 'Investor',
  'investors.colJoined': 'Joined',
  'investors.colProjects': 'Projects',
  'investors.colCommitted': 'Committed',
  'investors.colShare': 'Share',
  'investors.emptyTitle': 'No investor matches',
  'investors.emptyDescription': 'No investor on the register matches the filters in force.',
  'investors.subtotal': 'Subtotal',
  'investors.filteredTotal': 'Filtered total — {shown} of {total}',
  'investor.status.active': 'Active holder',
  'investor.kind.fund': 'Investment fund',
  'investor.notFoundTitle': 'Investor not found',
  'investor.notFoundHeading': 'No such investor',
  'investor.notFoundMessage': 'Nothing on the register matches "{id}". It may have been archived.',
  'investor.joined': 'Joined {date}',
  'investor.email': 'Email',
  'investor.phone': 'Phone',
  'investor.city': 'City',
  'investor.taxId': 'Tax id',
  'investor.developments': 'Developments',
  'investor.committed': 'Committed',
  'investor.outstanding': 'Outstanding',
  'investor.callsSettled.one': '{settled} of {count} capital call settled.',
  'investor.callsSettled.other': '{settled} of {count} capital calls settled.',
  'investor.participations': 'Participations',
  'investor.participationsHint': 'One row per development this investor holds a share in.',
  'investor.emptyTitle': 'No participation recorded',
  'investor.emptyPipeline':
    '{name} is on the register as a prospect with a zero-share record on {projects}. A participation with no share confers no holding and appears in no total.',
  'investor.emptyNone': '{name} holds no share in any development in the portfolio.',
  'investor.tableCaption':
    "This investor's participations, with the share, capital and capital calls against each development.",
  'investor.col.development': 'Development',
  'investor.col.joined': 'Joined',
  'investor.col.share': 'Share',
  'investor.col.committed': 'Committed',
  'investor.col.outstanding': 'Outstanding',
  'investor.col.calls': 'Calls',
  'investor.col.documents': 'Documents',
  'investor.totalAcross.one': 'Total across {count} development',
  'investor.totalAcross.other': 'Total across {count} developments',
  'investor.documentsPanel': 'Documents on file',
  'investor.documentsPanelHint': "What the office holds against this investor's positions.",
  'investor.noDocuments': 'Nothing is filed against a position that does not exist.',
  'investor.documentKinds': 'Subscription, capital call notices and statements',
  'investor.documentsTotal.one':
    '{count} document in total. The files themselves are held against the development, because a subscription agreement belongs to the position rather than to the person.',
  'investor.documentsTotal.other':
    '{count} documents in total. The files themselves are held against the development, because a subscription agreement belongs to the position rather than to the person.',
  'investor.readPanel': 'What they have read',
  'investor.readPanelHint.one': '{opened} of {count} published update opened.',
  'investor.readPanelHint.other': '{opened} of {count} published updates opened.',
  'investor.readershipTitle': 'Proposed capability — readership tracking, pending validation',
  'investor.readershipBody':
    'The platform does not yet record who opened a published update. What follows is the sample log used in the validation exercise (RF11, marked pending). It shows what the screen will do once the tracking is agreed and switched on — it is not a record of delivery.',
  'investor.nothingPublished': 'Nothing has been published to {name} on their developments yet.',
  'investor.publishedOn': 'published {date}',
  'investor.opened': 'Opened {date}',
  'investor.notOpened': 'Not opened',
  'investor.readProgress': 'Read {percent} of this update',
  'project.investors.reconciles': 'The cap table reconciles',
  'project.investors.mismatch': 'The cap table does not reconcile',
  'project.investors.reconcilesBody':
    'Investor contributions of {contributed} agree exactly with the {committed} of committed capital on the project record, and the {investorShare} held by investors plus the {builderShare} retained by the builder is the whole of the development.',
  'project.investors.mismatchBody':
    'Investor contributions of {contributed} are {difference} away from the project record, and the shares sum to {shareSum} against {expected} expected. This is a data defect and the figures below should not be published.',
  'project.investors.capTable': 'Cap table',
  'project.investors.capTableHint': 'Who holds {name}, and what each has committed and paid in.',
  'project.investors.emptyTitle': 'No investor holds a share yet',
  'project.investors.emptyBody':
    '{name} is funded entirely by the builder at this point. The first participation to carry a share appears here.',
  'project.investors.tableCaption':
    'The investors of {name}, with share, committed and contributed capital, outstanding balance and capital calls.',
  'project.investors.col.participant': 'Participant',
  'project.investors.col.joined': 'Joined',
  'project.investors.col.share': 'Share',
  'project.investors.col.committed': 'Committed',
  'project.investors.col.outstanding': 'Outstanding',
  'project.investors.col.callsSettled': 'Calls settled',
  'project.investors.col.documents': 'Documents',
  'project.investors.count.one': '{count} investor',
  'project.investors.count.other': '{count} investors',
  'project.investors.retained': 'Retained by the builder',
  'project.investors.retainedNote':
    "Outside the cap table: the builder's own share of {name} carries no participation and no capital call.",
  'project.investors.pipelineTitle': 'On the pipeline, with no share',
  'project.investors.pipelineHint':
    'Recorded against this development so the conversation is not lost, outside every figure above.',
  'project.investors.entered': 'Entered {date}',
  'project.investors.documentsShared.one': '{count} document shared',
  'project.investors.documentsShared.other': '{count} documents shared',
  'project.investors.shareBadge': '{share} share',
  'project.investors.pipelineNote':
    'A participation with a zero share is a prospect, not a holder. It is excluded from the cap table, from the share total and from every capital figure on this screen — a participation in the system is not a participation in the development.',
  'permissions.title': 'Permissions',
  'permissions.intro':
    'Five profiles, and what each one may reach. This is the model the platform enforces — the matrix below is the whole of it.',
  'permissions.metric.profiles': 'Profiles',
  'permissions.metric.mapped': 'Team members mapped',
  'permissions.metric.mappedHint.one': '{count} person in the team fixture.',
  'permissions.metric.mappedHint.other': '{count} people in the team fixture.',
  'permissions.metric.mayRelease': 'May approve a release',
  'permissions.metric.mayReleaseHint': 'Profiles whose publishing column reads Yes.',
  'permissions.metric.portal': 'With portal access',
  'permissions.metric.portalHint': 'Only the investor profile may enter the portal.',
  'permissions.matrixTitle': 'Permission matrix',
  'permissions.matrixHint': 'Select a profile to read what it can and cannot do.',
  'permissions.matrixCaption':
    'Permission matrix: each profile against project scope, field content, financial access, publishing and portal access',
  'permissions.matrixNote':
    'Yes and No are absolute. Any other wording is the condition the document sets for that profile — a phrase like "per authority" is a limit that has to be granted, not a default.',
  'permissions.col.profile': 'Profile',
  'permissions.col.projects': 'Projects',
  'permissions.col.field': 'Field content',
  'permissions.col.financial': 'Financial',
  'permissions.col.publishing': 'Publishing',
  'permissions.col.portal': 'Investor portal',
  'permissions.can': 'Can',
  'permissions.cannot': 'Cannot',
  'permissions.whoHolds': 'Who holds it',
  'permissions.holdersLabel': 'People holding the {name} profile',
  'permissions.noHolders':
    'Nobody in the team fixture is mapped to this profile. In this demo it is the account signed in to the platform — the owner of the portfolio.',
  'permissions.portalNote':
    "The portal is a separate product with its own four sections. An internal screen is not reachable from it, which is what the profile's portal column buys.",
  'permissions.rulesTitle': 'The rules behind the model',
  'permissions.rulesHint':
    'The six controls §14 asks for, in the words the platform enforces them in.',
  'permissions.cell.yes': 'Yes',
  'permissions.cell.no': 'No',
  'permissions.cell.onlyAssignedProjects': 'Only assigned projects',
  'permissions.cell.createMedia': 'Create photos, videos, notes',
  'permissions.cell.managedProjects': 'Projects they manage',
  'permissions.cell.consultConsolidate': 'Consult and consolidate',
  'permissions.cell.perAuthority': 'Per authority',
  'permissions.cell.mayPrepare': 'May prepare; approval per final rule',
  'permissions.cell.authorisedProjects': 'Authorised projects',
  'permissions.cell.consultWhenNeeded': 'Consult when needed',
  'permissions.cell.createConsultVerify': 'Create, consult, verify per authority',
  'permissions.cell.notNecessarily': 'Not necessarily',
  'permissions.cell.broad': 'Broad',
  'permissions.cell.onlyTheirParticipations': 'Only their participations',
  'permissions.cell.publishedContentOnly': 'Published content only',
  'permissions.cell.releasedFinancialsOnly': 'Released financial data only',
  'permissions.profile.fieldLead': 'Field lead',
  'permissions.profile.fieldLeadSummary':
    'On site, capturing what has happened. Cannot see money and cannot release anything.',
  'permissions.profile.manager': 'Manager / coordinator',
  'permissions.profile.managerSummary':
    'Runs the developments they manage and consolidates what the field sends up.',
  'permissions.profile.finance': 'Finance / backoffice',
  'permissions.profile.financeSummary':
    'Owns the money side: creates, consults and verifies — but publishing is not their act.',
  'permissions.profile.owner': 'Owner / principal',
  'permissions.profile.ownerSummary':
    'Sees the whole portfolio and is the profile the final publication approval lands on.',
  'permissions.profile.investorSummary':
    'The portal, and only the portal: their own participations, and only what has been released.',
  'permissions.fieldLead.can1':
    'Capture photos, videos and notes on the projects they are assigned to',
  'permissions.fieldLead.can2': 'Attach each capture to the phase it belongs to',
  'permissions.fieldLead.cannot1': 'Open a project they are not assigned to',
  'permissions.fieldLead.cannot2': 'See any financial figure',
  'permissions.fieldLead.cannot3': 'Prepare, approve or release a publication',
  'permissions.fieldLead.cannot4': 'Sign in to the investor portal',
  'permissions.manager.can1': 'Consult and consolidate the evidence captured on their projects',
  'permissions.manager.can2': 'Prepare a publication for review',
  'permissions.manager.can3': 'See financial detail within the authority they were granted',
  'permissions.manager.cannot1': 'Approve the final release of a publication',
  'permissions.manager.cannot2': 'Reach projects outside their portfolio',
  'permissions.manager.cannot3': 'Sign in to the investor portal',
  'permissions.finance.can1': 'Create, consult and verify financial records within their authority',
  'permissions.finance.can2': 'Open the evidence of an authorised project when the money needs it',
  'permissions.finance.cannot1': 'Reach a project they are not authorised for',
  'permissions.finance.cannot2':
    'Treat a verification as a publication — the two are separate acts',
  'permissions.finance.cannot3': 'Sign in to the investor portal',
  'permissions.owner.can1': 'Open every project in the portfolio',
  'permissions.owner.can2': 'Create, consult and verify across field content and financials',
  'permissions.owner.can3': 'Approve and release a publication to investors',
  'permissions.owner.cannot1':
    'Sign in to the investor portal — that is the investor’s own surface',
  'permissions.investor.can1': 'Open the projects they hold a participation in',
  'permissions.investor.can2': 'Read published updates, released financials and released documents',
  'permissions.investor.can3': 'See the opportunity pipeline opened to investors',
  'permissions.investor.cannot1':
    'See internal evidence, drafts, or any document that has not been released',
  'permissions.investor.cannot2': 'See another investor’s participation',
  'permissions.investor.cannot3': 'Create or approve anything',
  'permissions.rule.byProject': 'Access by project',
  'permissions.rule.byProjectBody':
    'A profile only ever opens the projects it is attached to. Scope is enforced on the request, not by hiding a link — a field lead cannot reach a project that is not on their list, and a manager cannot reach a project outside their portfolio.',
  'permissions.rule.byRole': 'Access by role',
  'permissions.rule.byRoleBody':
    'Inside a project, what a profile may do comes from its role: capture, consolidate, verify or approve. The matrix above is the whole model — there is no second set of rules applied in code that the client cannot see.',
  'permissions.rule.publication': 'Separation of publication',
  'permissions.rule.publicationBody':
    'Preparing, approving and releasing are three separate acts. An update is registered, reviewed, approved and only then published, and the person who prepares it need not be the one who approves it. The portal shows the published subset — nothing reaches an investor merely because it was entered.',
  'permissions.rule.investor': 'Restricted investor',
  'permissions.rule.investorBody':
    'An investor sees their own participations, published content, and released financial data — nothing else. Their portal is a different product with its own navigation, so no internal screen can be reached from it by accident.',
  'permissions.rule.traceability': 'Traceability',
  'permissions.rule.traceabilityBody':
    'Every release records who prepared it, who reviewed it, who approved it and when. Every investor access to a published update is logged, with how much of it was read.',
  'permissions.rule.verification': 'Evidence verification',
  'permissions.rule.verificationBody':
    'A document carries two independent states: whether it has been verified internally, and whether it has been released to investors. Both are required before it appears in the portal, which is why a verified document can still be invisible to an investor.',
  'permissions.verificationUnreleased.one':
    'On file today: {count} verified document not released to investors',
  'permissions.verificationUnreleased.other':
    'On file today: {count} verified documents not released to investors',
  'permissions.verificationReleased.one': '{count} released',
  'permissions.verificationReleased.other': '{count} released',
  'permissions.accessLogCaption': 'Investor access to published updates',
  'permissions.access.investor': 'Investor',
  'permissions.access.development': 'Development',
  'permissions.access.update': 'Update',
  'permissions.access.viewed': 'Viewed',
  'permissions.access.read': 'Read',

  /* ---- Publications, reports and notifications (GE) ---- */
  'publications.title': 'Publications',
  'publications.intro':
    'Everything the platform intends to tell investors, and how far each record has travelled towards being told. Registering a record is not publishing it: only the fourth state is visible on the other side.',
  'publications.gate.draft':
    'Internal only. No investor can see it, and no investor can be told about it.',
  'publications.gate.inReview': 'Captured and being checked. Still invisible to investors.',
  'publications.gate.approved': 'Cleared to release, not released. Approval is not publication.',
  'publications.gate.published': 'Visible to every investor holding a share in the development.',
  'publications.queue': 'Queue',
  'publications.filterLabel': 'Filter the publishing queue by state',
  'publications.filterAll': 'All {count}',
  'publications.filterStatus': '{status} {count}',
  'publications.visibleSummary': '{visible} of {total} records are visible to investors.',
  'publications.emptyTitle': 'Nothing in this state',
  'publications.emptyBody':
    'No publication in the queue currently sits in the state you filtered to.',
  'publications.badgeNotified': 'Investors notified',
  'publications.badgeQuiet': 'Released without notice',
  'publications.reaches.one': 'Reaches {count} investor on this development.',
  'publications.reaches.other': 'Reaches {count} investors on this development.',
  'publications.sendForReview': 'Send for review',
  'publications.requestChanges': 'Request changes',
  'publications.approve': 'Approve',
  'publications.withdrawApproval': 'Withdraw approval',
  'publications.publishToInvestors': 'Publish to investors',
  'publications.releasedNote':
    'Released {date} — the record is closed and cannot be edited in place.',
  'publications.stepPrepared': 'Prepared',
  'publications.stepReviewed': 'Reviewed',
  'publications.awaitingReviewer': 'Awaiting a reviewer',
  'publications.awaitingApproval': 'Awaiting approval',
  'publications.notReleased': 'Not released',
  'publications.progressLabel': 'Publication progress for {title}',
  'publications.photographs': 'Photographs',
  'publications.documents': 'Documents',
  'publications.spendToDate': 'Spend to date',
  'publications.varianceVsBudget': 'Variance vs budget',
  'publications.under': 'under',
  'publications.over': 'over',
  'publications.notifyLabel': 'Notify investors when this is released',
  'publications.hintNotified': 'Investors were notified when it went out.',
  'publications.hintQuiet': 'It went out without a notification.',
  'publications.hintWillNotify': 'A notification will be raised alongside the release.',
  'publications.hintWillBeQuiet': 'No notification will be raised — §13 allows a quiet release.',
  'publications.notifiedTitle': 'Investors will be notified',
  'publications.silentSetTitle': 'Silent release set',
  'publications.toggleOnBody': '{title} will raise a notification when published.',
  'publications.toggleOffBody': '{title} will publish without notifying anyone.',
  'publications.toast.sentTitle': 'Sent for review',
  'publications.toast.sentBody':
    '{title} is with the reviewer. It is still invisible to investors.',
  'publications.toast.approvedBody':
    '{title} is cleared to release. It becomes visible only when it is published.',
  'publications.toast.backToDraftTitle': 'Back to draft',
  'publications.toast.backToDraftBody':
    "{title} returned to the author. The reviewer's sign-off was cleared.",
  'publications.toast.withdrawnTitle': 'Approval withdrawn',
  'publications.toast.withdrawnBody':
    '{title} is back in review. The approval it carried no longer applies.',
  'publications.toast.publishedNotifiedTitle': 'Published and notified',
  'publications.toast.publishedQuietlyTitle': 'Published quietly',
  'publications.toast.notifiedBody.one': '{count} investor on {project} has been notified.',
  'publications.toast.notifiedBody.other': '{count} investors on {project} have been notified.',
  'publications.toast.quietBody.one':
    'Live for {count} investor, with no notification sent — §13 allows a routine update to go out silently.',
  'publications.toast.quietBody.other':
    'Live for {count} investors, with no notification sent — §13 allows a routine update to go out silently.',
  'publications.confirmTitle': 'Publish to investors?',
  'publications.confirmConfirm': 'Publish now',
  'publications.confirmCancel': 'Not yet',
  'publications.confirmBody.one':
    'becomes visible immediately to {count} investor holding a share in {project}. Publication cannot be undone: the record is closed and a correction has to go out as a new published update.',
  'publications.confirmBody.other':
    'become visible immediately to {count} investors holding a share in {project}. Publication cannot be undone: the record is closed and a correction has to go out as a new published update.',
  'publications.confirmDiscloses': 'Discloses',
  'publications.confirmSpent': '{amount} spent',
  'publications.confirmVariance': 'Variance',
  'publications.confirmCarries': 'Carries',
  'publications.countPhotos.one': '{count} photo',
  'publications.countPhotos.other': '{count} photos',
  'publications.countDocuments.one': '{count} document',
  'publications.countDocuments.other': '{count} documents',
  'publications.confirmApprovedBy': 'Approved by',
  'publications.confirmApprovedOn': '{name} on {date}',
  'publications.notifiedBody':
    'A notification is raised alongside the release, so it appears in their queue.',
  'publications.quietTitle': 'This will go out silently',
  'publications.quietBody':
    'No notification will be raised. §13 makes the notification selective on purpose — a routine update can be published without interrupting anyone. Cancel if you meant to announce it.',
  'project.publications.preparedBy': 'Prepared by {name}',
  'project.publications.reviewedBy': 'reviewed by {name}',
  'project.publications.approvedBy': 'approved by {name} on {date}',
  'project.publications.releasedOn': 'released {date}',
  'project.publications.queueMetric': 'In the internal queue',
  'project.publications.queueHint':
    'Every record, in every state, whether or not an investor may see it.',
  'project.publications.visibleMetric': 'Visible to investors',
  'project.publications.visibleHint':
    'Out of {total} records. The portal shows these and nothing else.',
  'project.publications.hiddenMetric': 'Not visible yet',
  'project.publications.hiddenHint':
    'Captured, and deliberately withheld. Being in the system is not being published.',
  'project.publications.queueTitle': 'Internal queue',
  'project.publications.queueDescription':
    'Every record on this development, with where each one stopped.',
  'project.publications.queueItemMeta':
    '{photos} · {documents} · would disclose {amount} ({variance} vs budget)',
  'project.publications.photos.one': '{count} photo',
  'project.publications.photos.other': '{count} photos',
  'project.publications.documents.one': '{count} document',
  'project.publications.documents.other': '{count} documents',
  'project.publications.releasedNotified': 'Released and investors were notified on {date}.',
  'project.publications.releasedQuietly': 'Released quietly on {date} — no notification was sent.',
  'project.publications.stillInternal':
    'No investor can see this yet. Only the released state crosses the gate.',
  'project.publications.feedTitle': 'Investor feed',
  'project.publications.feedDescription':
    'The updates list in the portal for {project} — the released subset alone.',
  'project.publications.feedEmpty':
    'Nothing has been released on this development. An investor opening the portal today finds no updates at all.',
  'project.publications.notificationSent': 'Notification sent',
  'project.publications.quietRelease': 'Quiet release',
  'project.publications.photographs.one': '{count} photograph',
  'project.publications.photographs.other': '{count} photographs',
  'project.publications.feedItemMeta': '{photographs} · {documents} · spend to date {amount}',
  'project.publications.audience.one':
    '{count} investor holds a share in this development and sees exactly this list. The {records} records above them do not exist as far as the portal is concerned.',
  'project.publications.audience.other':
    '{count} investors hold a share in this development and see exactly this list. The {records} records above them do not exist as far as the portal is concerned.',
  'project.publications.notifiedTitle': 'Notified to investors',
  'project.publications.notifiedDescription.one':
    '{count} of {total} milestones on this development raised a notification.',
  'project.publications.notifiedDescription.other':
    '{count} of {total} milestones on this development raised a notification.',
  'project.publications.notifiedEmpty':
    'No milestone on this development has raised an investor notification yet.',
  'project.publications.notifiedBadge': 'Notified',
  'project.publications.quietTitle': 'Reached without a notification',
  'project.publications.quietDescription':
    'Milestones that moved the build and were recorded — and told to nobody.',
  'project.publications.quietEmpty':
    'Every milestone reached on this development so far has been notified. Nothing has gone by quietly.',
  'project.publications.quietBadge': 'No notification',
  'project.publications.countMilestones.one': '{count} milestone',
  'project.publications.countMilestones.other': '{count} milestones',
  'project.publications.countUpdates.one': '{count} published update',
  'project.publications.countUpdates.other': '{count} published updates',
  'project.publications.silenceNote':
    '§13 makes the notification selective on purpose: {milestones} and {updates} on this development went out without interrupting anyone, and every one of them is still visible in the portal. Silence is not withholding.',
  'project.publications.ahead.one': '{count} milestone still ahead, with {next} on {date}.',
  'project.publications.ahead.other': '{count} milestones still ahead, with {next} on {date}.',
  'project.publications.nextInspection': 'the next inspection',
  'project.publications.nextOne': 'the next one',
  'project.publications.otherSideTitle': 'Open the other side',
  'project.publications.otherSideDescription':
    'The portal renders the released subset exactly as the investor receives it.',
  'project.publications.viewAsInvestor': 'View {project} as an investor sees it',
  'project.publications.holderNote.one':
    '— signed in as a holder, with only the {count} released update and their published documents.',
  'project.publications.holderNote.other':
    '— signed in as a holder, with only the {count} released updates and their published documents.',
  'reports.title': 'Reports',
  'reports.intro':
    'Every figure in a report comes from the same records this platform shows, so a report cannot disagree with the screen it was taken from.',
  'reports.generateTitle': 'Generate a report',
  'reports.generateDescription':
    'Choose what the report covers. It is built from the database at the moment it is requested.',
  'reports.development': 'Development',
  'reports.searchDevelopments': 'Search developments…',
  'reports.scope': 'Scope',
  'reports.scopeLabel': 'Report scope',
  'reports.period': 'Period',
  'reports.searchPeriods': 'Search periods…',
  'reports.generate': 'Generate report',
  'reports.generateHint': 'Select a development, a scope and a period to build the request.',
  'reports.requestedTitle': 'Requested in this session',
  'reports.requestedEmpty': 'No reports requested yet.',
  'reports.requestedAt': 'Requested {date}',
  'reports.registerTitle': 'Register of generated reports',
  'reports.registerDescription': 'What the platform has already produced for each development.',
  'reports.kind': 'Kind',
  'reports.kindLabel': 'Filter by kind',
  'reports.kind.period': 'Period',
  'reports.kind.phase': 'Phase',
  'reports.kind.final': 'Final',
  'reports.showing': 'Showing {shown} of {total} reports.',
  'reports.emptyTitle': 'No reports match these filters',
  'reports.emptyBody':
    'Nothing in the register matches the kind, development and period selected together.',
  'reports.heldInTotal.one': '{count} report is held in total.',
  'reports.heldInTotal.other': '{count} reports are held in total.',
  'reports.caption': 'Generated reports, filtered by kind, development and period',
  'reports.column.report': 'Report',
  'reports.column.generated': 'Generated',
  'reports.column.by': 'By',
  'reports.column.pages': 'Pages',
  'reports.column.state': 'State',
  'reports.notGenerated': 'Not generated',
  'reports.footnote':
    'The final report is generated automatically once a development is complete and sold, so it is listed here before it has been produced.',
  'reports.toast.requestedTitle': 'Report requested',
  'reports.allDevelopments': 'All developments',
  'reports.wholeProject': 'Whole project',
  'reports.allPeriods': 'All periods',
  'report.kind.period': 'Period report',
  'report.kind.phase': 'Phase report',
  'report.kind.final': 'Final report',
  'report.notFoundTitle': 'Report not found',
  'report.notFoundHeading': 'No such report',
  'report.notFoundMessage':
    'Nothing matches "{id}". It may have been withdrawn, or the link may be out of date.',
  'report.backToRegister': 'Back to the report register',
  'report.unknownDevelopment': 'Unknown development',
  'report.pages.one': '{pages} page',
  'report.pages.other': '{pages} pages',
  'report.notGeneratedTitle': 'This report has not been generated yet',
  'report.notGeneratedBody':
    "The final report is produced from the project's own database records once the development is complete and the last unit has sold. Until then there is nothing to preview — no figures, no narrative and no document exist to show.",
  'report.financialTitle': 'Financial summary',
  'report.financialDescription':
    'Every figure below derives from the same phase schedule as the project dashboard.',
  'report.budgeted': 'Budgeted',
  'report.budgetedHint': 'The phased budget across every trade.',
  'report.spentHint': 'Approved cost posted against the schedule.',
  'report.varianceHint': 'Actual spend less the {amount} budgeted for the phases under way.',
  'report.unspent': 'Unspent balance',
  'report.unspentHint': 'Contributed capital less what has actually been spent.',
  'report.spendAgainstBudget': '{title} — spend against budget',
  'report.updatesHeading': 'Published updates',
  'report.updatesDescription':
    'Only updates that have been reviewed and approved reach the investor portal. Anything still in draft or in review stays internal, so this section shows fewer updates than the publication queue does.',
  'report.updatesEmptyTitle': 'No published updates yet',
  'report.updatesEmptyBody':
    'Nothing has been approved and published for this development, so the investor portal has shown this investor nothing.',
  'report.publishedOn': 'Published {date}',
  'report.preparedBy': 'Prepared by',
  'report.approvedBy': 'Approved by',
  'report.photos': 'Photos',
  'report.documents': 'Documents',
  'report.evidenceTitle': 'Published evidence',
  'report.evidenceDescription.one':
    '{count} published photo, grouped in phase order. This is the subset cleared for the investor portal, not the full internal library — the fixtures carry no image source, so each record reads as a captioned entry.',
  'report.evidenceDescription.other':
    '{count} published photos, grouped in phase order. This is the subset cleared for the investor portal, not the full internal library — the fixtures carry no image source, so each record reads as a captioned entry.',
  'report.phasesTitle': 'Phase costs',
  'report.phasesDescription':
    'Budgeted and actual cost per phase. The totals row ties to the financial summary above.',
  'report.phasesCaption': 'Budgeted and actual cost for each phase',
  'report.column.phase': 'Phase',
  'report.column.budgeted': 'Budgeted',
  'report.column.actual': 'Actual',
  'report.column.variance': 'Variance',
  'report.column.used': 'Used',
  'report.phaseCount.one': '{count} phase',
  'report.phaseCount.other': '{count} phases',
  'notifications.title': 'Notifications',
  'notifications.intro':
    'What the office was told, and — separately — what the investor was told. A notification is raised by a decision, so a record can change state without interrupting anybody.',
  'notifications.logMetric': 'In the log',
  'notifications.unreadMetric': 'Unread',
  'notifications.unreadNone': 'Everything has been read.',
  'notifications.unreadSome': 'Waiting to be looked at.',
  'notifications.milestonesMetric': 'Milestones notified',
  'notifications.milestonesHint': 'Out of {total} milestones reached so far.',
  'notifications.quietMetric': 'Released quietly',
  'notifications.quietHint':
    'Published to investors with no notification — §13 makes the announcement optional.',
  'notifications.history': 'Notification history',
  'notifications.historyHint': "The office's own queue, most recent event first.",
  'notifications.markedTitle': 'All marked as read',
  'notifications.markedBody.one': '{count} notification cleared.',
  'notifications.markedBody.other': '{count} notifications cleared.',
  'notifications.category': 'Category',
  'notifications.filterLabel': 'Filter notifications by category',
  'notifications.filterAll': 'All {count}',
  'notifications.filterCategory': '{category} {count}',
  'notifications.emptyTitle': 'Nothing in this category',
  'notifications.emptyBody': 'No notification in the log carries the category you filtered to.',
  'notifications.unreadSuffix': '(unread)',
  'notifications.category.info': 'Info',
  'notifications.category.success': 'Success',
  'notifications.category.warning': 'Warning',
  'notifications.category.error': 'Error',
  'notifications.read': 'Read',
  'notifications.markAsRead': 'Mark as read',
  'notifications.reachedTitle': 'What reached the investor',
  'notifications.reachedDescription':
    'Every milestone in the platform, with the notification decision against it.',
  'notifications.ahead': 'Ahead',
  'notifications.notified': 'Notified',
  'notifications.noNotification': 'No notification',
  'notifications.summary.one':
    '{count} milestone raised an investor notification and {quiet} were reached without one. A milestone is a fact about the build; whether it is announced is a separate decision, taken here.',
  'notifications.summary.other':
    '{count} milestones raised an investor notification and {quiet} were reached without one. A milestone is a fact about the build; whether it is announced is a separate decision, taken here.',
  'notifications.preferencesTitle': 'Delivery preferences',
  'notifications.preferencesDescription': "A manager's settings for what is announced, and how.",
  'notifications.selectiveTitle': 'Selective by design',
  'notifications.selectiveBody':
    "Suppressing a notification never suppresses the record. A published update with no notification is still in the investor's portal the moment it is released — the setting only decides whether anyone is interrupted to say so.",
  'notifications.channelLabel': 'Delivery channel',
  'notifications.channelSavedTitle': 'Delivery channel saved',
  'notifications.channelSavedBody': 'Investor notifications now go out by {channel}.',
  'notifications.channel.email': 'Email',
  'notifications.channel.emailHint': 'Each notification is sent as it happens.',
  'notifications.channel.emailPhrase': 'email',
  'notifications.channel.inApp': 'In-app only',
  'notifications.channel.inAppHint':
    'Nothing leaves the platform; holders see it when they sign in.',
  'notifications.channel.inAppPhrase': 'in-app only',
  'notifications.channel.digest': 'Weekly digest',
  'notifications.channel.digestHint': 'One message a week, collecting everything that happened.',
  'notifications.channel.digestPhrase': 'weekly digest',
  'notifications.eventsLabel': 'Events that raise a notification',
  'notifications.event.milestone': 'Milestone reached',
  'notifications.event.milestoneHint':
    'A phase or inspection milestone is recorded against a development.',
  'notifications.event.variance': 'Budget variance beyond {threshold}',
  'notifications.event.varianceHint':
    'Actuals drift from the phased budget by more than the threshold.',
  'notifications.event.release': 'Published update released',
  'notifications.event.releaseHint':
    'An update cleared the gate and became visible in the investor portal.',
  'notifications.event.call': 'Capital call issued',
  'notifications.event.callHint': 'A funding call goes out to the investors holding a share.',
  'notifications.eventEnabled': 'Notification enabled',
  'notifications.eventSilenced': 'Notification silenced',
  'notifications.eventOnBody': '{event} will raise a notification.',
  'notifications.eventOffBody':
    '{event} will no longer interrupt anyone. The record still appears in the portal.',
  'notifications.eventsSummary':
    '{active} of {total} event types raise an active notification, delivered by {channel}.',

  /* ---- Investor portal (GF) ---- */
  'portal.home.title': 'Your portfolio',
  'portal.home.summary.one': 'One development in which you hold a participation',
  'portal.home.summary.other': '{count} developments in which you hold a participation',
  'portal.home.lastUpdated': ', most recently updated on {date}',
  'portal.home.totalsHeading': 'Your totals',
  'portal.home.committed': 'Committed',
  'portal.home.committedHint': 'The total you have committed across your participations.',
  'portal.home.contributed': 'Contributed',
  'portal.home.contributedHint': 'Capital called and settled to date.',
  'portal.home.shareValue': 'Value of your share',
  'portal.home.shareValueHint':
    'Your ownership percentage of each development, applied to the capital committed to it.',
  'portal.home.unspent': 'Unspent across your projects',
  'portal.home.unspentHint':
    'Capital committed to your developments and not yet spent, at your share.',
  'portal.home.developmentsHeading': 'Your developments',
  'portal.home.share': '{share} share',
  'portal.home.progress': 'Progress',
  'portal.home.currentPhase': 'Current phase',
  'portal.home.latestUpdate': 'Latest update',
  'portal.home.nonePublished': 'None published',
  'portal.home.viewDevelopment': 'View development',
  'portal.home.latestPanelTitle': 'Most recently released update',
  'portal.home.released': 'Released {date}',
  'portal.home.photos': 'Photos',
  'portal.home.documents': 'Documents',
  'portal.home.approvedBy': 'Approved by',
  'portal.home.releasedLabel': 'Released',
  'portal.home.readUpdate': 'Read the full update',
  'portal.home.developmentFallback': 'Development',
  'portal.project.share': '{share} share',
  'portal.project.progress': 'Progress',
  'portal.project.notInPortfolio': 'Not in your portfolio',
  'portal.project.refusedTitle': 'This development is not part of your portfolio',
  'portal.project.refusedBody':
    'Investor access is limited to the developments you hold a participation in. If you were expecting to see this one, your relationship manager can confirm your positions.',
  'portal.project.backToPortfolio': 'Back to your portfolio',
  'portal.project.backLink': 'Your portfolio',
  'portal.project.participationTitle': 'Your participation',
  'portal.project.participatingSince': 'Participating since {date}.',
  'portal.project.yourShare': 'Your share',
  'portal.project.yourShareHint': 'Your ownership percentage of this development.',
  'portal.project.committed': 'Committed',
  'portal.project.contributed': 'Contributed',
  'portal.project.contributedHint': 'Capital called and settled to date.',
  'portal.project.capitalCalls': 'Capital calls',
  'portal.project.callsSettledHint': 'settled of those issued',
  'portal.project.ownershipTitle': 'Ownership',
  'portal.project.retainedByBuilder': 'Retained by the builder',
  'portal.project.heldByInvestors': 'Held by investors under participation agreements',
  'portal.project.yourParticipation': 'Your participation',
  'portal.project.ownershipNote':
    'Individual investor positions are disclosed only to the investor who holds them, so the register is shown here without names or amounts.',
  'portal.project.scheduleTitle': 'Schedule',
  'portal.project.scheduleDescription':
    'Planned against actual, by phase. The phases are not sequential — each is positioned on the schedule window, so an overlap is where two phases really do run at once.',
  'portal.project.legendActualInProgress': 'Actual, in progress',
  'portal.project.legendActualCompleted': 'Actual, completed',
  'portal.project.budgeted': '{amount} budgeted',
  'portal.project.actual': '{amount} actual',
  'portal.project.noSpend': 'no spend yet',
  'portal.project.phaseFallback': 'General',
  'portal.project.milestonesTitle': 'Milestones',
  'portal.project.milestonesNext': 'Next',
  'portal.project.noUpcomingMilestones':
    'No further milestones are scheduled for this development.',
  'portal.project.milestonesReached': 'Reached and notified',
  'portal.project.noReachedMilestones': 'No milestones have been notified to investors yet.',
  'portal.project.approvedTitle': 'Approved financial summary',
  'portal.project.approvedDescription':
    'The figures as they were disclosed to you, not as they stand in the platform today.',
  'portal.project.spendToDate': 'Spend to date',
  'portal.project.spendToDateHint': 'Total project cost disclosed in the published update below.',
  'portal.project.againstForecast': 'Against forecast',
  'portal.project.againstForecastHint':
    'Negative means the period settled under the forecast published for it.',
  'portal.project.yourShareUnspent': 'Your share, unspent',
  'portal.project.yourShareUnspentHint':
    'Capital committed to this development and not yet spent, at your share.',
  'portal.project.disclosedIn': 'Disclosed in',
  'portal.project.disclosedRest':
    ', period {period}, approved by {approvedBy} on {approvedAt} and released to you on {publishedAt}.',
  'portal.project.noApprovedYet':
    'No financial figures have been approved for release on this development yet. Nothing appears here until an update carrying them has been approved and published.',
  'portal.project.evidenceTitle': 'Published evidence',
  'portal.project.evidenceCount.one':
    "{count} item cleared for release to you, from the development's photographic record.",
  'portal.project.evidenceCount.other':
    "{count} items cleared for release to you, from the development's photographic record.",
  'portal.project.noEvidence': 'No evidence has been released on this development yet.',
  'portal.project.capturedBy': 'Captured by {name}',
  'portal.project.documentsTitle': 'Released documents',
  'portal.project.documentsDescription':
    'Documents authorised for your participation in this development.',
  'portal.project.openDocuments': 'Open documents',
  'portal.project.noDocuments': 'No documents have been released on this development yet.',
  'portal.updates.title': 'Published updates',
  'portal.updates.intro':
    'Each update below was reviewed and approved before it reached you, and carries the name of the person who approved it. Nothing in the publication queue appears here.',
  'portal.updates.filterLabel': 'Filter updates by development',
  'portal.updates.bothDevelopments': 'Both developments',
  'portal.updates.count.one': '{count} published update',
  'portal.updates.count.other': '{count} published updates',
  'portal.updates.countScoped.one': '{count} published update for {project}',
  'portal.updates.countScoped.other': '{count} published updates for {project}',
  'portal.updates.emptyTitle': 'No published updates',
  'portal.updates.emptyBody': 'Nothing has been approved for release on this selection yet.',
  'portal.updates.publishedBadge': 'Published {date}',
  'portal.updates.photos': 'Photos',
  'portal.updates.documents': 'Documents',
  'portal.updates.approvedBy': 'Approved by',
  'portal.updates.period': 'Period',
  'portal.updates.readUpdate': 'Read the full update',
  'portal.updates.developmentFallback': 'Development',
  'portal.update.notAvailableHeading': 'Update not available',
  'portal.update.refusedTitle': 'This update is not available to you',
  'portal.update.refusedBody':
    'It has either not been published, or it belongs to a development outside your portfolio. Only approved updates for your own participations are shown here.',
  'portal.update.backToUpdates': 'Back to published updates',
  'portal.update.developmentFallback': 'Development',
  'portal.update.backLink': 'Published updates',
  'portal.update.publishedBadge': 'Published {date}',
  'portal.update.periodBadge': 'Period covered: {period}',
  'portal.update.approvalTitle': 'Approved for release to you',
  'portal.update.preparedBy': 'Prepared by',
  'portal.update.reviewedBy': 'Reviewed by',
  'portal.update.approvedBy': 'Approved by',
  'portal.update.approvedOn': 'Approved on',
  'portal.update.recordNote':
    'This is the approved record of the period. It was released to you on {date} after the review above, and it is the version you are entitled to rely on.',
  'portal.update.periodTitle': 'The period',
  'portal.update.figuresTitle': 'Figures disclosed in this update',
  'portal.update.figuresDescription': 'The cost position as it was approved for this period.',
  'portal.update.spendToDate': 'Spend to date',
  'portal.update.spendToDateHint': 'Total project cost at the close of this period.',
  'portal.update.againstForecast': 'Against forecast',
  'portal.update.againstForecastHint':
    'Negative means the period settled under the forecast published for it.',
  'portal.update.evidenceTitle': 'Evidence released with this update',
  'portal.update.evidencePeriod':
    'Published photographs from {period}. Not every photograph taken on site is cleared for release, and only the cleared ones appear here.',
  'portal.update.evidenceRecord': "Published photographs from this development's cleared record.",
  'portal.update.noEvidence': 'No photographs have been released on this development yet.',
  'portal.update.phaseFallback': 'General',
  'portal.update.capturedBy': 'Captured by {name}',
  'portal.update.documentsTitle': 'Documents released with this update',
  'portal.update.documentsDescription':
    'Authorised files for this development. Files are requested from the documents screen.',
  'portal.update.openDocuments': 'Open documents',
  'portal.update.noDocuments': 'No documents have been released yet.',
  'portal.documents.title': 'Documents and reports',
  'portal.documents.intro':
    'The documents authorised for your participations, and the reports generated from them. A document appears here once it has been verified and released, not when it was received.',
  'portal.documents.filterLabel': 'Filter documents by development',
  'portal.documents.bothDevelopments': 'Both developments',
  'portal.documents.count.one': '{count} authorised document',
  'portal.documents.count.other': '{count} authorised documents',
  'portal.documents.reportCount.one': '{count} published report',
  'portal.documents.reportCount.other': '{count} published reports',
  'portal.documents.downloadTitle': 'Download prepared',
  'portal.documents.downloadBody': '{name} — file delivery is not enabled in this environment.',
  'portal.documents.downloadLabel': 'Download {name}',
  'portal.documents.perProject.one': '{count} document authorised for your participation.',
  'portal.documents.perProject.other': '{count} documents authorised for your participation.',
  'portal.documents.listLabel': 'Authorised documents for {project}',
  'portal.documents.emptyProject': 'No documents have been released on this development yet.',
  'portal.documents.reportsTitle': 'Published reports',
  'portal.documents.reportsDescription':
    'Generated from the approved record of each period and phase.',
  'portal.documents.noReports': 'No reports have been published for this selection yet.',
  'portal.documents.reportScope.period': 'Period report',
  'portal.documents.reportScope.phase': 'Phase report',
  'portal.documents.reportScope.final': 'Final report',
  'portal.documents.period': 'Period',
  'portal.documents.pages': 'Pages',
  'portal.documents.generated': 'Generated',
  'portal.documents.preparedBy': 'Prepared by {name}',
  'portal.documents.finalTitle': 'Final report',
  'portal.documents.finalDescription':
    'Issued once, after the development is sold — the complete account of it rather than of a period.',
  'portal.documents.notIssued': 'Not yet issued',
  'portal.documents.finalBody':
    'When the development closes, your final report replaces the period reports with a single document: the realised outcome against the forecast, the closing accounts, and the full photographic record from acquisition to sale. It is generated from the same approved record as everything above, so nothing in it can disagree with what you were told along the way.',
  'portal.documents.releasesAfterSale': 'Releases after the sale, projected {date}',
  'portal.opportunities.title': 'New opportunities',
  'portal.opportunities.intro':
    'Developments being studied for the future. This area stays quiet on purpose — it is a note of what is coming, kept out of the way of the investments you already hold.',
  'portal.opportunities.alertTitle': 'Nothing here is open for participation',
  'portal.opportunities.alertBody':
    'These developments are still being studied, and the terms on which an investor could take part have not yet been set. No capital is being sought and no offer is being made. Your relationship manager will contact you directly if one of these is opened, and you will not be asked to respond to anything on this page.',
  'portal.opportunities.emptyTitle': 'No opportunities at present',
  'portal.opportunities.emptyBody':
    'There are no developments under study for future participation right now.',
  'portal.opportunities.projectedStart': 'Projected start {date}',
  'portal.opportunities.notOpen': 'Not open for participation.',
  /* ==== END GROUPED CATALOGUE ENTRIES ==== */
} satisfies Record<string, string>;

/**
 * Every key the application can ask for, plural forms included.
 *
 * A countable string is stored as `X.one` beside `X.other`, the two forms
 * `Intl.PluralRules` selects between — and the count-carrying lookup falls back
 * to `X` itself when it holds only one wording, which is how "No unread
 * notifications." sits beside a pair without needing an empty second half.
 */
export type AppTranslationKey = keyof typeof en;

/**
 * The BASE key of a countable string — `shell.notifications.unread` for the
 * pair stored under `.one` and `.other`.
 *
 * Derived rather than declared, so the two cannot drift: a plural pair written
 * into the catalogue is addressable by its base from that moment, and no key
 * that is not a plural becomes addressable by one.
 */
export type AppPluralKey = PluralBase<AppTranslationKey>;

/* A helper type parameter, not a bare alias: a conditional distributes over a
   union only when the checked side is a type PARAMETER, and the alias above
   would otherwise be tested once, as a whole, and come back `never`. */
type PluralBase<Key extends string> = Key extends `${infer Base}.one`
  ? Base
  : Key extends `${infer Base}.other`
    ? Base
    : never;
